import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

/** Wraps a component in a higher-order component that ensures that the component gets rendered into a React Portal at
 * the bottom of the DOM.
 * 
 * This is useful to ensure a component appears outside of some other component, e.g. when the component uses
 * position:fixed, but the component's ancestors use transform: translate, which messages up position fixed
 */
export default function withPortal<T extends React.ComponentType<any>>(
  WrappedComponent: T,
  portalId: string = 'portal-root'
): React.FC<React.ComponentProps<T>> {
  const PortalWrapper: React.FC<React.ComponentProps<T>> = (props) => {
    const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
      let element = document.getElementById(portalId);

      if (!element) {
        element = document.createElement('div');
        element.id = portalId;
        document.body.appendChild(element);
      }

      setPortalElement(element);

      return () => {
        if (element?.parentNode) {
          element.parentNode.removeChild(element);
        }
      };
    }, [portalId]);

    if (!portalElement) return null;

    return ReactDOM.createPortal(<WrappedComponent {...props} />, portalElement);
  };

  return PortalWrapper;
}